import { PaginatedResponse, useApi } from '@/api';
import { Link, TableCellBottom, Asset, MarketTags } from '@/components';
import {
  CurrencyValue,
  PercentageValue,
  DataTable,
  TableSkeleton
} from '@blockanalitica/ui';
import {
  TableColumn,
  TableData
} from '@blockanalitica/ui/package/types/src/components/molecules/DataTable';
import { useParams } from 'react-router';
import { TableProps } from '@blockanalitica/ui/package/types/src/hooks/useTableProps';
import { TablePagination } from '@blockanalitica/ui/package/types/src/components/atoms/Table/types';

type Market = {
  underlying_symbol: string;
  underlying_name: string;
  underlying_address: string;
  total_supply: string;
  total_supply_usd: string;
  total_supply_change: string | null;
  total_supply_usd_change: string | null;
  total_supply_change_percentage: string | null;
  total_supply_usd_change_percentage: string | null;
  total_borrow: string;
  total_borrow_usd: string;
  total_borrow_change: string | null;
  total_borrow_usd_change: string | null;
  total_borrow_change_percentage: string | null;
  total_borrow_usd_change_percentage: string | null;
  supply_apy: string;
  supply_apy_1d: string | null;
  supply_apy_7d: string | null;
  supply_apy_30d: string | null;
  supply_apy_90d: string | null;
  supply_apy_365d: string | null;
  supply_apy_change: string | null;
  supply_apy_change_percentage: string | null;
  borrow_apy: string;
  borrow_apy_1d: string | null;
  borrow_apy_7d: string | null;
  borrow_apy_30d: string | null;
  borrow_apy_90d: string | null;
  borrow_apy_365d: string | null;
  borrow_apy_1d_change: string | null;
  borrow_apy_7d_change: string | null;
  borrow_apy_30d_change: string | null;
  borrow_apy_90d_change: string | null;
  borrow_apy_365d_change: string | null;
  supply_apy_1d_change: string | null;
  supply_apy_7d_change: string | null;
  supply_apy_30d_change: string | null;
  supply_apy_90d_change: string | null;
  supply_apy_365d_change: string | null;
  borrow_apy_change: string | null;
  borrow_apy_change_percentage: string | null;
  utilization_rate: string;
  utilization_rate_change: string | null;
  utilization_rate_change_percentage: string | null;
  status: {
    borrow: string;
    collateral: string;
    supply: string;
    frozen: string;
  };
};

type MarketsTableProps = {
  pagination?: TablePagination;
  p: number;
  p_size: number;
  order?: string;
  apyOption?: string;
  sorting?: TableProps['sorting'];
};

function getSupplySorting(apyOption: string) {
  switch (apyOption) {
    case '':
      return 'supply_apy';
    case '_1d':
      return 'supply_apy_1d';
    case '_7d':
      return 'supply_apy_7d';
    case '_30d':
      return 'supply_apy_30d';
    case '_90d':
      return 'supply_apy_90d';
    case '_365d':
      return 'supply_apy_365d';
    default:
      return 'supply_apy';
  }
}
function getBorrowSorting(apyOption: string) {
  switch (apyOption) {
    case '':
      return 'borrow_apy';
    case '_1d':
      return 'borrow_apy_1d';
    case '_7d':
      return 'borrow_apy_7d';
    case '_30d':
      return 'borrow_apy_30d';
    case '_90d':
      return 'borrow_apy_90d';
    case '_365d':
      return 'borrow_apy_365d';
    default:
      return 'borrow_apy';
  }
}

export default function MarketsTable({
  sorting = undefined,
  pagination = undefined,
  p,
  p_size,
  order = '-total_supply_usd',
  apyOption = ''
}: MarketsTableProps) {
  const { network } = useParams() as { network: string };

  const [data, error] = useApi<PaginatedResponse<Market[]>>({
    url: `/markets/`,
    queryParams: {
      page: p,
      limit: p_size,
      sort: order
    }
  });

  if (error) {
    throw error;
  }

  if (!data) {
    return <TableSkeleton rows={5} columns={6} />;
  }

  const columns: TableColumn[] = [
    {
      header: '',
      getCell: (row) => (
        <Asset
          symbol={row.underlying_symbol}
          address={row.underlying_address}
          network={network}
          name={row.underlying_name}
          iconSize="3"
        />
      ),
      columnSize: ['minmax(150px, auto)', 'minmax(250, auto)']
    },
    {
      header: 'Supplied',
      getCell: (row) => (
        <CurrencyValue
          value={row.total_supply_usd}
          options={{ currency: 'USD' }}
        />
      ),
      getCellBottom: (row) => (
        <TableCellBottom checkValue={row.total_supply_usd_change}>
          <CurrencyValue
            value={row.total_supply_usd_change}
            options={{ currency: 'USD', trend: true }}
          />
        </TableCellBottom>
      ),
      cellAlign: 'flex-end',
      headerAlign: 'flex-end',
      sortingField: 'total_supply_usd'
    },
    {
      header: 'Borrowed',
      getCell: (row) =>
        row.status['borrow'] === 'borrow_disabled' ? (
          '-'
        ) : (
          <CurrencyValue
            value={row.total_borrow_usd}
            options={{ currency: 'USD' }}
          />
        ),
      getCellBottom: (row) =>
        row.status['borrow'] === 'borrow_disabled' ? (
          '-'
        ) : (
          <TableCellBottom checkValue={row.total_borrow_usd_change}>
            <CurrencyValue
              value={row.total_borrow_usd_change}
              options={{ currency: 'USD', trend: true }}
            />
          </TableCellBottom>
        ),
      cellAlign: 'flex-end',
      headerAlign: 'flex-end',
      sortingField: 'total_borrow_usd'
    },
    {
      header: 'Deposit APY',

      getCell: (row) => (
        <PercentageValue value={row[`supply_apy${apyOption}`]} />
      ),
      getCellBottom: (row) => (
        <TableCellBottom checkValue={row[`supply_apy${apyOption}_change`]}>
          <PercentageValue
            value={row[`supply_apy${apyOption}_change`]}
            options={{
              trend: true
            }}
          />
        </TableCellBottom>
      ),
      cellAlign: 'flex-end',
      headerAlign: 'flex-end',
      sortingField: getSupplySorting(apyOption)
    },
    {
      header: 'Borrow APY',

      getCell: (row) =>
        row.status['borrow'] === 'borrow_disabled' ? (
          '-'
        ) : (
          <PercentageValue value={row[`borrow_apy${apyOption}`]} />
        ),
      getCellBottom: (row) =>
        row.status['borrow'] === 'borrow_disabled' ? (
          '-'
        ) : (
          <TableCellBottom checkValue={row[`borrow_apy${apyOption}_change`]}>
            <PercentageValue
              value={row[`borrow_apy${apyOption}_change`]}
              options={{
                trend: true
              }}
            />
          </TableCellBottom>
        ),
      cellAlign: 'flex-end',
      headerAlign: 'flex-end',
      sortingField: getBorrowSorting(apyOption)
    },
    {
      header: 'Utilization',
      getCell: (row) =>
        row.status['borrow'] === 'borrow_disabled' ? (
          '-'
        ) : (
          <PercentageValue value={row.utilization_rate} />
        ),
      getCellBottom: (row) =>
        row.status['borrow'] === 'borrow_disabled' ? (
          '-'
        ) : (
          <TableCellBottom checkValue={row.utilization_rate_change}>
            <PercentageValue
              value={row.utilization_rate_change}
              options={{
                trend: true
              }}
            />
          </TableCellBottom>
        ),
      cellAlign: 'flex-end',
      headerAlign: 'flex-end',
      sortingField: 'utilization_rate'
    },
    {
      header: '',
      getCell: (row) => <MarketTags status={row.status} />,
      cellAlign: 'flex-end',
      headerAlign: 'flex-end'
    }
  ];

  const tablePagination = pagination
    ? {
        ...pagination,
        totalRecords: data.pagination.total
      }
    : undefined;

  return (
    <DataTable
      rows={data.results}
      columns={columns}
      getKey={(row) => `${row.underlying_address}`}
      rowLinkProps={{
        to: (row: TableData) => `/markets/${row.underlying_address}`,
        // @ts-ignore
        linkComponent: Link
      }}
      pagination={tablePagination}
      sorting={sorting ? sorting : undefined}
    />
  );
}
